import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type PdfViewSection = 'executive-summary' | 'personas' | 'media-plan' | 'analysis-and-strategy'

interface MultiTabPdfProgress {
  currentTab: string
  currentStep: number
  totalSteps: number
  isComplete: boolean
  errors: string[]
}

interface PdfViewState {
  activePdfViews: Record<string, PdfViewSection[]>
  ganttChartImages: Record<string, { image: string; campaignsHash: string }>
  multiTabGeneration: Record<
    string,
    {
      isStaffPdf: boolean
      isGenerating: boolean
      progress: MultiTabPdfProgress | null
      error: string | null
    }
  >
}

const initialState: PdfViewState = {
  activePdfViews: {},
  ganttChartImages: {},
  multiTabGeneration: {}
}

const pdfViewSlice = createSlice({
  name: 'pdfView',
  initialState,
  reducers: {
    togglePdfView: (state, action: PayloadAction<{ planId: string; section: PdfViewSection }>) => {
      const { planId, section } = action.payload

      if (!state.activePdfViews[planId]) {
        state.activePdfViews[planId] = []
      }

      const currentSections = state.activePdfViews[planId]
      const sectionIndex = currentSections.indexOf(section)

      if (sectionIndex > -1) {
        currentSections.splice(sectionIndex, 1)
      } else {
        currentSections.push(section)
      }
    },
    setPdfView: (state, action: PayloadAction<{ planId: string; section: PdfViewSection; isActive: boolean }>) => {
      const { planId, section, isActive } = action.payload

      if (!state.activePdfViews[planId]) {
        state.activePdfViews[planId] = []
      }

      const currentSections = state.activePdfViews[planId]
      const sectionIndex = currentSections.indexOf(section)

      if (isActive && sectionIndex === -1) {
        currentSections.push(section)
      } else if (!isActive && sectionIndex > -1) {
        currentSections.splice(sectionIndex, 1)
      }
    },
    clearPdfViews: (state, action: PayloadAction<string>) => {
      const planId = action.payload
      delete state.activePdfViews[planId]
    },
    setGanttChartImage: (state, action: PayloadAction<{ planId: string; image: string; campaignsHash: string }>) => {
      const { planId, image, campaignsHash } = action.payload
      state.ganttChartImages[planId] = { image, campaignsHash }
    },
    clearGanttChartImage: (state, action: PayloadAction<string>) => {
      const planId = action.payload
      delete state.ganttChartImages[planId]
    },
    invalidateGanttChartImage: (state, action: PayloadAction<{ planId: string; campaignsHash: string }>) => {
      const { planId, campaignsHash } = action.payload
      const existing = state.ganttChartImages[planId]
      if (existing && existing.campaignsHash !== campaignsHash) {
        delete state.ganttChartImages[planId]
      }
    },
    startMultiTabPdfGeneration: (state, action: PayloadAction<{ planId: string; isStaffPdf: boolean }>) => {
      const { planId, isStaffPdf } = action.payload
      state.multiTabGeneration[planId] = {
        isStaffPdf,
        isGenerating: true,
        progress: null,
        error: null
      }
    },
    updateMultiTabPdfProgress: (state, action: PayloadAction<{ planId: string; progress: MultiTabPdfProgress }>) => {
      const { planId, progress } = action.payload
      if (state.multiTabGeneration[planId]) {
        state.multiTabGeneration[planId].progress = progress
      }
    },
    completeMultiTabPdfGeneration: (state, action: PayloadAction<string>) => {
      const planId = action.payload
      if (state.multiTabGeneration[planId]) {
        state.multiTabGeneration[planId].isGenerating = false
      }
    },
    setMultiTabPdfError: (state, action: PayloadAction<{ planId: string; error: string }>) => {
      const { planId, error } = action.payload
      if (state.multiTabGeneration[planId]) {
        state.multiTabGeneration[planId].isGenerating = false
        state.multiTabGeneration[planId].error = error
      }
    },
    clearMultiTabPdfState: (state, action: PayloadAction<string>) => {
      const planId = action.payload
      delete state.multiTabGeneration[planId]
    }
  }
})

export const { togglePdfView, setPdfView, clearPdfViews, setGanttChartImage, clearGanttChartImage, invalidateGanttChartImage, startMultiTabPdfGeneration, updateMultiTabPdfProgress, completeMultiTabPdfGeneration, setMultiTabPdfError, clearMultiTabPdfState } = pdfViewSlice.actions

export const selectIsPdfViewActive = (state: { pdfView: PdfViewState }, planId: string, section: PdfViewSection): boolean => {
  return state.pdfView.activePdfViews[planId]?.includes(section) ?? false
}

export const selectGanttChartImage = (state: { pdfView: PdfViewState }, planId: string): string | undefined => {
  return state.pdfView.ganttChartImages[planId]?.image
}

export const selectGanttChartImageData = (state: { pdfView: PdfViewState }, planId: string) => {
  return state.pdfView.ganttChartImages[planId]
}

export const selectMultiTabPdfGenerationState = (state: { pdfView: PdfViewState }, planId: string) => {
  return (
    state.pdfView.multiTabGeneration[planId] || {
      isGenerating: false,
      progress: null,
      error: null
    }
  )
}

export const selectMultiTabPdfProgress = (state: { pdfView: PdfViewState }, planId: string): MultiTabPdfProgress | null => {
  return state.pdfView.multiTabGeneration[planId]?.progress ?? null
}

export default pdfViewSlice.reducer
